import { Injectable, signal } from '@angular/core';
import { Internal } from '../decorators/app.decorators';
import { VCPM, PcsSizingEngineResponse, clusterEntityKindToClusterType, resolveClusterMembers, syncReferencedHardwareConfiguration, buildWorkloadNameById } from 'pcs-domain';
import type { ClusterEntityKind } from 'pcs-domain';
import { AppConstants } from '../data-models/app-constants';

export const MANAGEMENT_DOMAIN_STAGED_KEY = 'MGMT';

export type StagedChange =
  | {
      kind: 'profile';
      domain: 'workload';
      requirementsId: string;
      workloadId: string;
      profileCode: string;
      discardsReadyNode: boolean;
    }
  | {
      kind: 'profile';
      domain: 'management';
      profileCode: string;
      discardsReadyNode: boolean;
    }
  | {
      kind: 'readynode';
      domain: 'workload';
      requirementsId: string;
      workloadId: string;
      hardwareConfiguration: VCPM.HardwareConfiguration;
      result: PcsSizingEngineResponse.WorkloadRecommendation;
    }
  | {
      kind: 'readynode';
      domain: 'management';
      hardwareConfiguration: VCPM.HardwareConfiguration;
      result: PcsSizingEngineResponse.WorkloadRecommendation;
    }
  | {
      // Cluster View's cluster-scoped counterpart to the 'readynode' variants above - a Recalculate
      // against a customized ReadyNode (HCI/SON) or ESXi (CON) spec that fans out across every
      // workload referencing the cluster, instead of a single workload/the management domain. See
      // applyStagedChangeToProject's 'cluster-readynode' branch, which mirrors what used to be
      // RecalculateClusterHardwareConfiguration's reducer body 1:1.
      kind: 'cluster-readynode';
      requirementsId: string;
      clusterId: string;
      clusterKind: ClusterEntityKind;
      hardwareConfiguration: VCPM.HardwareConfiguration;
      resultsByWorkloadId: Map<string, PcsSizingEngineResponse.WorkloadRecommendation>;
    };

export type SaveState = 'idle' | 'saving' | 'success';

interface ReadyNodePreview {
  hardwareConfiguration: VCPM.HardwareConfiguration;
  result: PcsSizingEngineResponse.WorkloadRecommendation;
  /** Present only once a Logic Bound step has moved this preview off its baseline. */
  logicBound?: LogicBoundStaging;
}

/**
 * What a Logic Bound exploration needs beyond the hardware it produced.
 *
 * `baseline` is what Logic Bound Reset returns to - the Recalculate preview the first step was taken
 * from, or undefined when that was the saved recommendation (resolved from the project on read, never
 * copied here). It is captured once, on the first step, and never moves with later steps; only a
 * Recalculate (which replaces the whole preview) or a Save (which unstages it) moves it.
 *
 * `minNodes` is a host count the user set through the NODES control. It is re-sent with every later
 * step so a resource step does not silently discard it.
 */
export interface LogicBoundStaging {
  baseline?: { hardwareConfiguration: VCPM.HardwareConfiguration; result: PcsSizingEngineResponse.WorkloadRecommendation };
  minNodes?: number;
}

interface ClusterReadyNodePreview {
  requirementsId: string;
  clusterId: string;
  clusterKind: ClusterEntityKind;
  hardwareConfiguration: VCPM.HardwareConfiguration;
  resultsByWorkloadId: Map<string, PcsSizingEngineResponse.WorkloadRecommendation>;
  /** Present only once a Logic Bound step has moved this preview off its baseline. */
  logicBound?: ClusterLogicBoundStaging;
}

/**
 * LogicBoundStaging's cluster-scoped counterpart: a step on a cluster moves every workload sharing it, so
 * its baseline - the cluster Recalculate preview the first step was taken from, or undefined when that was
 * the saved recommendation - carries every member's result, exactly as that preview did.
 */
export interface ClusterLogicBoundStaging {
  baseline?: { hardwareConfiguration: VCPM.HardwareConfiguration; resultsByWorkloadId: Map<string, PcsSizingEngineResponse.WorkloadRecommendation> };
  minNodes?: number;
}

/**
 * Per-workload staging slot. tierPick and readyNodePreview are INDEPENDENT and both survive
 * regardless of which card is currently highlighted - a recalculated ReadyNode preview must still
 * be there to compare against after browsing back to a tier card, exactly as it was before this
 * service existed (see the original pendingReadyNodeSaveMap doc comment in workload-domain.component
 * history for why: "the user can freely compare tiers against a customized ReadyNode before
 * deciding what to save"). `active` tracks which of the two the user currently has highlighted -
 * i.e. which one Save Changes would actually commit - and mirrors WorkloadRecommendation's own
 * selectedProfileCode (tier code vs the SELECTED_READYNODE sentinel) without needing a reference
 * back to that object.
 */
interface WorkloadStaging {
  domain: 'workload' | 'management' | 'cluster';
  requirementsId?: string;
  workloadId?: string;
  tierPick?: { profileCode: string; discardsReadyNode: boolean };
  readyNodePreview?: ReadyNodePreview;
  clusterReadyNodePreview?: ClusterReadyNodePreview;
  active: 'tier' | 'readynode' | 'none';
}

const SUCCESS_DISPLAY_MS = 1800;

/**
 * Holds output-page selections (tier profile picks, ReadyNode recalculate previews) that have not
 * yet been persisted to VCPM. Deliberately NOT part of NGXS/currentProject - staging state here and
 * only ever writing it into currentProject on an explicit "Save Changes" commit is what keeps an
 * unsaved selection from being accidentally persisted (the bug class PCS-2516/PCS-2518 fix for).
 *
 * Save is scoped per tab (one workload, or the management domain): activeKey() tracks which tab is
 * currently visible so a page-level Save button (living above the tabs) knows what it is enabling
 * and committing, without threading that state through @Output()s across component levels.
 */
@Internal()
@Injectable({ providedIn: 'root' })
export class OutputStagedChangesService {
  private readonly staging = signal<ReadonlyMap<string, WorkloadStaging>>(new Map());
  private readonly saveStates = signal<ReadonlyMap<string, SaveState>>(new Map());
  private readonly currentActiveKey = signal<string | null>(null);
  private readonly successTimers = new Map<string, ReturnType<typeof setTimeout>>();

  static keyFor(requirementsId: string, workloadId: string): string {
    return `${requirementsId}:${workloadId}`;
  }

  /** Distinct namespace from keyFor()'s `requirementsId:workloadId` - a cluster-scoped change is
   *  keyed by the cluster entity itself, not any one of the (possibly several) workloads it fans
   *  out to. Same `${kind}::${id}` format as WorkloadDomainComponent's own getClusterEntityKey(),
   *  so a resolved cluster entity can be staged/looked-up without a separate key translation. */
  static clusterKeyFor(clusterKind: ClusterEntityKind, clusterId: string): string {
    return `${clusterKind}::${clusterId}`;
  }

  stageWorkloadProfile(requirementsId: string, workloadId: string, profileCode: string, discardsReadyNode: boolean): void {
    const key = OutputStagedChangesService.keyFor(requirementsId, workloadId);
    this.update(key, (entry) => ({
      ...entry,
      domain: 'workload',
      requirementsId,
      workloadId,
      tierPick: { profileCode, discardsReadyNode },
      active: 'tier',
    }));
  }

  stageManagementProfile(profileCode: string, discardsReadyNode: boolean): void {
    this.update(MANAGEMENT_DOMAIN_STAGED_KEY, (entry) => ({
      ...entry,
      domain: 'management',
      tierPick: { profileCode, discardsReadyNode },
      active: 'tier',
    }));
  }

  stageWorkloadReadyNode(
    requirementsId: string,
    workloadId: string,
    hardwareConfiguration: VCPM.HardwareConfiguration,
    result: PcsSizingEngineResponse.WorkloadRecommendation,
  ): void {
    const key = OutputStagedChangesService.keyFor(requirementsId, workloadId);
    this.update(key, (entry) => ({
      ...entry,
      domain: 'workload',
      requirementsId,
      workloadId,
      readyNodePreview: { hardwareConfiguration, result },
      active: 'readynode',
    }));
  }

  stageManagementReadyNode(
    hardwareConfiguration: VCPM.HardwareConfiguration,
    result: PcsSizingEngineResponse.WorkloadRecommendation,
  ): void {
    this.update(MANAGEMENT_DOMAIN_STAGED_KEY, (entry) => ({
      ...entry,
      domain: 'management',
      readyNodePreview: { hardwareConfiguration, result },
      active: 'readynode',
    }));
  }

  /** Cluster View's cluster-scoped counterpart to stageWorkloadReadyNode/stageManagementReadyNode -
   *  see the 'cluster-readynode' StagedChange variant's own doc comment. Keyed by clusterKeyFor(),
   *  never keyFor(), so it never collides with any single workload's own staged tier/ReadyNode pick. */
  stageClusterReadyNode(
    requirementsId: string,
    clusterId: string,
    clusterKind: ClusterEntityKind,
    hardwareConfiguration: VCPM.HardwareConfiguration,
    resultsByWorkloadId: Map<string, PcsSizingEngineResponse.WorkloadRecommendation>,
  ): void {
    const key = OutputStagedChangesService.clusterKeyFor(clusterKind, clusterId);
    this.update(key, (entry) => ({
      ...entry,
      domain: 'cluster',
      clusterReadyNodePreview: { requirementsId, clusterId, clusterKind, hardwareConfiguration, resultsByWorkloadId },
      active: 'readynode',
    }));
  }

  /**
   * Stages one applied Logic Bound step on the management domain as its ReadyNode preview - the same slot a
   * management Recalculate writes, so the Recalculate panel, every card and Save Changes see the step with no
   * path of their own. A Recalculate replaces the preview wholesale, which is what drops the exploration (and
   * its baseline) when the user re-customizes. (A workload domain's steps are staged per cluster instead -
   * see stageClusterLogicBoundStep.)
   */
  stageManagementLogicBoundStep(
    hardwareConfiguration: VCPM.HardwareConfiguration,
    result: PcsSizingEngineResponse.WorkloadRecommendation,
    minNodes: number | undefined,
  ): void {
    this.update(MANAGEMENT_DOMAIN_STAGED_KEY, (entry) => {
      const previous = entry.readyNodePreview;
      const baseline = previous?.logicBound
        ? previous.logicBound.baseline
        : previous && { hardwareConfiguration: previous.hardwareConfiguration, result: previous.result };
      return {
        ...entry,
        domain: 'management',
        readyNodePreview: { hardwareConfiguration, result, logicBound: { baseline, minNodes } },
        active: 'readynode',
      };
    });
  }

  getLogicBound(key: string): LogicBoundStaging | undefined {
    return this.staging().get(key)?.readyNodePreview?.logicBound;
  }

  /**
   * Logic Bound Reset: back to the baseline - the Recalculate preview the exploration started from,
   * or, when it started from the saved recommendation, no preview at all. A no-op without an
   * exploration, so it can never discard a plain Recalculate preview.
   */
  resetLogicBound(key: string): void {
    const logicBound = this.getLogicBound(key);
    if (!logicBound) return;
    if (logicBound.baseline) {
      this.update(key, (entry) => ({ ...entry, readyNodePreview: { ...logicBound.baseline! } }));
    } else {
      this.clearReadyNodePreview(key);
    }
  }

  /**
   * Stages one applied Logic Bound step on a cluster as the cluster's ReadyNode preview - the same slot a
   * cluster Recalculate writes, so every workload sharing the cluster, the Recalculate panel, every card and
   * Save Changes see the step with no path of their own. A cluster Recalculate replaces the preview
   * wholesale, which is what drops the exploration (and its baseline) when the user re-customizes.
   */
  stageClusterLogicBoundStep(
    requirementsId: string,
    clusterId: string,
    clusterKind: ClusterEntityKind,
    hardwareConfiguration: VCPM.HardwareConfiguration,
    resultsByWorkloadId: Map<string, PcsSizingEngineResponse.WorkloadRecommendation>,
    minNodes: number | undefined,
  ): void {
    const key = OutputStagedChangesService.clusterKeyFor(clusterKind, clusterId);
    this.update(key, (entry) => {
      const previous = entry.clusterReadyNodePreview;
      const baseline = previous?.logicBound
        ? previous.logicBound.baseline
        : previous && { hardwareConfiguration: previous.hardwareConfiguration, resultsByWorkloadId: previous.resultsByWorkloadId };
      return {
        ...entry,
        domain: 'cluster',
        clusterReadyNodePreview: {
          requirementsId, clusterId, clusterKind, hardwareConfiguration, resultsByWorkloadId, logicBound: { baseline, minNodes },
        },
        active: 'readynode',
      };
    });
  }

  getClusterLogicBound(clusterKind: ClusterEntityKind, clusterId: string): ClusterLogicBoundStaging | undefined {
    return this.getClusterReadyNodePreview(clusterKind, clusterId)?.logicBound;
  }

  /**
   * Logic Bound Reset on a cluster: back to the baseline - the cluster Recalculate preview the exploration
   * started from, or, when it started from the saved recommendation, no preview at all. A no-op without an
   * exploration, so it can never discard a plain cluster Recalculate preview.
   */
  resetClusterLogicBound(clusterKind: ClusterEntityKind, clusterId: string): void {
    const preview = this.getClusterReadyNodePreview(clusterKind, clusterId);
    if (!preview?.logicBound) return;
    const key = OutputStagedChangesService.clusterKeyFor(clusterKind, clusterId);
    const baseline = preview.logicBound.baseline;
    if (baseline) {
      const { requirementsId } = preview;
      this.update(key, (entry) => ({
        ...entry,
        clusterReadyNodePreview: { requirementsId, clusterId, clusterKind, ...baseline },
      }));
    } else {
      // A cluster key holds nothing but its preview
      this.unstage(key);
    }
  }

  /** The user clicked back onto the 4th ("Selected ReadyNode") card - makes any existing preview
   *  the active/committable one again, without discarding a tier pick that's still sitting there. */
  activateReadyNodeCard(key: string): void {
    this.update(key, (entry) => ({ ...entry, active: 'readynode' }));
  }

  /** Browsing to a tier card: the preview stays staged (the 4th card still shows it) but is no longer
   *  what Save would commit. */
  deactivateReadyNodeCard(key: string): void {
    if (!this.staging().has(key)) return;
    this.update(key, (entry) => ({ ...entry, active: entry.tierPick ? 'tier' : 'none' }));
  }

  /** The ReadyNode preview for a key, independent of which card is currently active - the 4th card
   *  always shows it (see WorkloadStaging's own doc comment), regardless of browsing to a tier. */
  getReadyNodePreview(key: string): ReadyNodePreview | undefined {
    return this.staging().get(key)?.readyNodePreview;
  }

  /** Cluster View's cluster-scoped counterpart to getReadyNodePreview() - looked up via
   *  clusterKeyFor(), not keyFor(). */
  getClusterReadyNodePreview(clusterKind: ClusterEntityKind, clusterId: string): ClusterReadyNodePreview | undefined {
    return this.staging().get(OutputStagedChangesService.clusterKeyFor(clusterKind, clusterId))?.clusterReadyNodePreview;
  }

  /** The user clicked a tier card whose code matches what's already persisted - nothing new to
   *  stage for the tier itself, but this remains the currently highlighted card (mirrors
   *  stageWorkloadProfile's active:'tier'), independent of any separate ReadyNode preview still
   *  sitting on the 4th card - that preview only becomes committable again once the user actively
   *  chooses/changes the ReadyNode (activateReadyNodeCard/stageWorkloadReadyNode). */
  clearTierPick(key: string): void {
    this.update(key, (entry) => ({ ...entry, tierPick: undefined, active: 'tier' }));
  }

  /** Drops the preview only (e.g. a Recalculate whose result matches what's already persisted -
   *  nothing new to save) without touching a tier pick that may also be staged for this key. */
  clearReadyNodePreview(key: string): void {
    const entry = this.staging().get(key);
    if (!entry?.readyNodePreview) return;
    const next: WorkloadStaging = { ...entry, readyNodePreview: undefined };
    next.active = next.active === 'readynode' ? (next.tierPick ? 'tier' : 'none') : next.active;
    this.write(key, next);
  }

  /** Clears everything staged for a key - both the tier pick and the ReadyNode preview become
   *  stale once either one is actually committed (see completeSave()). */
  unstage(key: string): void {
    if (!this.staging().has(key)) return;
    const next = new Map(this.staging());
    next.delete(key);
    this.staging.set(next);
  }

  /** The currently active/committable change for a key, in the same shape CommitOutputSelections
   *  expects - or undefined if there's nothing the active card would actually persist. */
  get(key: string): StagedChange | undefined {
    const entry = this.staging().get(key);
    if (!entry) return undefined;
    if (entry.active === 'tier' && entry.tierPick) {
      return entry.domain === 'workload'
        ? { kind: 'profile', domain: 'workload', requirementsId: entry.requirementsId!, workloadId: entry.workloadId!, ...entry.tierPick }
        : { kind: 'profile', domain: 'management', ...entry.tierPick };
    }
    if (entry.active === 'readynode' && entry.readyNodePreview) {
      // Only what gets committed - the Logic Bound bookkeeping stays behind.
      const { hardwareConfiguration, result } = entry.readyNodePreview;
      return entry.domain === 'workload'
        ? { kind: 'readynode', domain: 'workload', requirementsId: entry.requirementsId!, workloadId: entry.workloadId!, hardwareConfiguration, result }
        : { kind: 'readynode', domain: 'management', hardwareConfiguration, result };
    }
    if (entry.active === 'readynode' && entry.clusterReadyNodePreview) {
      // Only what gets committed - the Logic Bound bookkeeping stays behind.
      const { requirementsId, clusterId, clusterKind, hardwareConfiguration, resultsByWorkloadId } = entry.clusterReadyNodePreview;
      return { kind: 'cluster-readynode', requirementsId, clusterId, clusterKind, hardwareConfiguration, resultsByWorkloadId };
    }
    return undefined;
  }

  hasStagedChange(key: string | null): boolean {
    return !!key && !!this.get(key);
  }

  /** True when what Save would commit for `key` is a Recalculate result. A bare tier pick is
   *  excluded from the leave guard; the save alert uses hasStagedChange to include tier picks. */
  hasUnsavedRecalculation(key: string | null): boolean {
    const change = key ? this.get(key) : undefined;
    return !!change && change.kind !== 'profile';
  }

  /**
   * Every committable staged change across all keys, for computing the summary the user is looking
   * at - which must reflect selections staged on ANY tab, not just the active one. Deliberately
   * NOT what a save uses: CommitOutputSelections applies only the single change being committed,
   * so one workload's Save never persists another workload's unsaved pick.
   */
  allStagedChanges(): StagedChange[] {
    return [...this.staging().keys()]
      .map((key) => this.get(key))
      .filter((change): change is StagedChange => !!change);
  }

  discardsReadyNode(key: string | null): boolean {
    const change = key ? this.get(key) : undefined;
    return change?.kind === 'profile' && change.discardsReadyNode;
  }

  /** Whether committing `key` should be confirmed before persisting - either because it discards a
   *  saved ReadyNode (see discardsReadyNode), or because it's cluster-scoped and so affects every
   *  workload referencing the cluster, not just whichever one the user currently has open (mirrors
   *  the old per-cluster Save modal's always-confirm behavior - see describeClusterReadyNodeChange,
   *  which gives the confirmation copy the affected names to show). A Management domain ReadyNode
   *  recalculation is always confirmed too (see isManagementReadyNodeChange). */
  requiresConfirmation(key: string | null): boolean {
    const change = key ? this.get(key) : undefined;
    return this.discardsReadyNode(key) || change?.kind === 'cluster-readynode' || this.isManagementReadyNodeChange(key);
  }

  /** True when `key` holds a recalculated ReadyNode preview for the Management domain. */
  isManagementReadyNodeChange(key: string | null): boolean {
    const change = key ? this.get(key) : undefined;
    return change?.kind === 'readynode' && change.domain === 'management';
  }

  /** For a 'cluster-readynode' change, the cluster's own display name and every affected workload's
   *  name - lets the Save Changes confirmation name them, same information the old per-cluster
   *  modal (WorkloadDomainComponent's handleClusterRecalculationSaveModal) used to show. undefined
   *  for any other kind, or if the cluster/its members can no longer be resolved from `project`. */
  describeClusterReadyNodeChange(key: string | null, project: VCPM.Project): { clusterName: string; workloadNames: string[]; hardwareLabel: 'ReadyNode' | 'ESXi' } | undefined {
    const change = key ? this.get(key) : undefined;
    if (change?.kind !== 'cluster-readynode' || !project.data.workloadDomains) {
      return undefined;
    }
    const domain = project.data.workloadDomains.find((d) => String(d.id) === String(change.requirementsId));
    const cluster = change.clusterKind === 'STORAGE'
      ? project.data.clusterConfigurations?.find((c) => c.id === change.clusterId)
      : domain?.clusterConfigurations?.find((c) => c.id === change.clusterId && c.type === change.clusterKind);
    if (!cluster) {
      return undefined;
    }
    const clusterType = clusterEntityKindToClusterType(change.clusterKind);
    const members = resolveClusterMembers(project.data.workloadDomains, change.clusterId, clusterType);
    const recommendations = project.data.output?.recommendation?.workloadDomainsRecommendation?.result?.recommendations;
    const nameById = buildWorkloadNameById(recommendations);
    const workloadNames = members.map((member) => nameById.get(`${member.requirementsId}:${member.workloadId}`) ?? nameById.get(member.workloadId) ?? member.workloadId);
    // CON (compute) clusters are sized on ESXi servers; HCI and SON (storage) clusters on ReadyNodes.
    return { clusterName: cluster.name, workloadNames, hardwareLabel: change.clusterKind === 'COMPUTE' ? 'ESXi' : 'ReadyNode' };
  }

  setActiveKey(key: string | null): void {
    this.currentActiveKey.set(key);
  }

  activeKey(): string | null {
    return this.currentActiveKey();
  }

  saveState(key: string | null): SaveState {
    if (!key) return 'idle';
    return this.saveStates().get(key) ?? 'idle';
  }

  beginSave(key: string): void {
    this.clearSuccessTimer(key);
    this.setSaveState(key, 'saving');
  }

  /** Unstages the committed change and briefly shows a success state before reverting to idle. */
  completeSave(key: string): void {
    this.unstage(key);
    this.setSaveState(key, 'success');
    this.clearSuccessTimer(key);
    this.successTimers.set(
      key,
      setTimeout(() => {
        this.setSaveState(key, 'idle');
        this.successTimers.delete(key);
      }, SUCCESS_DISPLAY_MS),
    );
  }

  /** Leaves the staged change in place so the user can retry. */
  failSave(key: string): void {
    this.setSaveState(key, 'idle');
  }

  private update(key: string, mutate: (entry: WorkloadStaging) => WorkloadStaging): void {
    const existing = this.staging().get(key) ?? { domain: 'workload', active: 'none' as const };
    this.write(key, mutate(existing));
  }

  private write(key: string, entry: WorkloadStaging): void {
    const next = new Map(this.staging());
    next.set(key, entry);
    this.staging.set(next);
  }

  private setSaveState(key: string, state: SaveState): void {
    const next = new Map(this.saveStates());
    if (state === 'idle') {
      next.delete(key);
    } else {
      next.set(key, state);
    }
    this.saveStates.set(next);
  }

  private clearSuccessTimer(key: string): void {
    const timer = this.successTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.successTimers.delete(key);
    }
  }
}

/**
 * Applies one committed staged change to `project` IN PLACE, returning it - or undefined when the
 * targeted workload recommendation isn't in this project at all (nothing to commit).
 *
 * Called from the commitOutputSelections/recalculateClusterHardwareConfiguration reducers in
 * ProjectDetailsState, which persist the result via UpdateProject and then keep currentProject's
 * engine-derived fields intact across that PUT round trip - see
 * ProjectDetailsState.persistAndKeepEngineDerivedOutput's own doc comment for why that second step
 * is necessary (the backend PUT response does not carry the sizing engine's derived
 * `profileRecommendations` candidates).
 *
 * A 'profile' change (Small/Medium/Large) is NOT tied to any hardware SKU, so committing one flips
 * the workload/management-domain input to hardwareConfigMode 'no-preference'. The last ReadyNode is
 * deliberately RETAINED on selectedReadyNodeRecommendation so the "Selected ReadyNode" 4th card
 * survives the save and remains selectable - see the profile branch below for why nothing is
 * retained for a workload that never had a ReadyNode at all. The rewritten
 * hardwareConfiguration MUST retain the existing commonConfig, or project-schema.transform.ts's
 * legacy-flat migration guard zeroes it on next load (no commonConfig + no cpuBasedConfig/
 * readyNodeBasedConfig/mode looks like a pre-mode-field legacy project to that guard).
 *
 * A 'cluster-readynode' change is the odd one out - it doesn't target one workloadRecomm/
 * workloadInput at all, it fans out across every workload referencing the cluster (see
 * applyClusterReadyNodeChange below, moved verbatim from what used to be
 * RecalculateClusterHardwareConfiguration's own reducer body), so it's handled as an early,
 * separate branch entirely.
 */
export function applyStagedChangeToProject(project: VCPM.Project, change: StagedChange): VCPM.Project | undefined {
  if (change.kind === 'cluster-readynode') {
    return applyClusterReadyNodeChange(project, change);
  }

  const workloadRecomm = change.domain === 'workload'
    ? project.data.output?.recommendation?.workloadDomainsRecommendation?.result?.recommendations
      ?.find((r: PcsSizingEngineResponse.Recommendation) => r.requirementsId === change.requirementsId)
      ?.workloadRecommendations.find((w: PcsSizingEngineResponse.WorkloadRecommendation) => w.workloadId === change.workloadId)
    : project.data.output?.recommendation?.managementDomainRecommendation?.result?.recommendations?.[0]
      ?.workloadRecommendations?.[0];

  if (!workloadRecomm) {
    return undefined;
  }

  const workloadInput = change.domain === 'workload'
    ? project.data.workloadDomains
      ?.find((d) => String(d.id) === String(change.requirementsId))
      ?.workloadList?.find((w) => String(w.id) === String(change.workloadId))
    : undefined;

  if (change.kind === 'profile') {
    const previousHardwareConfiguration = change.domain === 'workload'
      ? workloadInput?.hardwareConfiguration
      : project.data.managementDomain?.configuration?.hardwareConfiguration;

    // Retain whatever ReadyNode this workload/domain was last tied to, so the "Selected ReadyNode"
    // 4th card survives a tier save and the user can switch back to it instead of having to
    // re-pick the node from scratch. The tier is still what gets SIZED - selectedProfileCode below
    // is the plain tier code, so getEffectiveWorkloadRecommendation() resolves to the tier
    // candidate and the retained ReadyNode is only a selectable card (it also stays out of the
    // exported report, which gates on the sentinel - see withSavedReadyNodeSizing).
    //
    // Nothing is invented where there is nothing to retain: a genuine No Preference workload has
    // no ReadyNode until the user picks one via Recalculate (which lands in
    // selectedReadyNodeRecommendation and is covered by the first condition). The presence of an
    // actual readyNodeSelection is the test - NOT hardwareConfigMode alone, since a readynode-based
    // input still awaiting its selection has no node either and must not get a phantom card.
    if (!workloadRecomm.selectedReadyNodeRecommendation
      && previousHardwareConfiguration?.hardwareConfigMode === 'readynode-based'
      && previousHardwareConfiguration.readyNodeBasedConfig?.readyNodeSelection) {
      // The raw top-level `clusters` of a readynode-based recommendation ARE the configured
      // ReadyNode's own sizing (the same source getProfileCards() builds its 4th card from for a
      // never-recalculated ReadyNode workload); the tier candidates and selection bookkeeping are
      // stripped so this reads as a plain sizing result, exactly like a Recalculate-produced one.
      const readyNodeResult: PcsSizingEngineResponse.WorkloadRecommendation = JSON.parse(JSON.stringify(workloadRecomm));
      delete readyNodeResult.profileRecommendations;
      delete readyNodeResult.selectedProfileCode;
      delete readyNodeResult.selectedReadyNodeRecommendation;
      workloadRecomm.selectedReadyNodeRecommendation = {
        hardwareConfiguration: JSON.parse(JSON.stringify(previousHardwareConfiguration)),
        result: readyNodeResult,
        recalculatedAt: new Date().toISOString(),
      };
    }

    workloadRecomm.selectedProfileCode = change.profileCode;
    // Mirrors the shape the wizard itself produces when the user picks No Preference
    // (workload-configuration.component.ts clearReadyNodeFormControls()) - null-valued
    // cpuBasedConfig/readyNodeSelection fields there come from FormGroup.getRawValue() on
    // disabled controls, which is why HardwareConfiguration's declared types (string, not
    // string | null) don't line up 1:1 with the actual runtime shape; same `as unknown as`
    // escape hatch as no-preference-hardware-customization.component.ts:222.
    const noPreferenceHardwareConfiguration = {
      hardwareConfigMode: 'no-preference',
      cpuBasedConfig: { cpuVendor: null, cpuGeneration: null, cpuSeries: null, cpuCategory: null, cpuModel: null },
      readyNodeBasedConfig: {
        readyNodeSelection: null,
        recommendedProfileName: '',
        searchFilters: { vendorId: '', profile: '', serverModel: '', generationId: '', freeText: '' },
      },
      commonConfig: change.domain === 'workload'
        ? workloadInput?.hardwareConfiguration?.commonConfig
        : project.data.managementDomain?.configuration?.hardwareConfiguration?.commonConfig,
    } as unknown as VCPM.HardwareConfiguration;

    if (change.domain === 'workload') {
      if (workloadInput) {
        workloadInput.hardwareConfiguration = noPreferenceHardwareConfiguration;
      }
    } else if (project.data.managementDomain?.configuration) {
      project.data.managementDomain.configuration.hardwareConfiguration = noPreferenceHardwareConfiguration;
    }
  } else {
    workloadRecomm.selectedReadyNodeRecommendation = {
      hardwareConfiguration: change.hardwareConfiguration,
      result: change.result,
      recalculatedAt: new Date().toISOString(),
    };
    workloadRecomm.selectedProfileCode = AppConstants.SELECTED_READYNODE_PROFILE_CODE;

    if (change.domain === 'workload') {
      if (workloadInput) {
        workloadInput.hardwareConfiguration = change.hardwareConfiguration;
      }
    } else if (project.data.managementDomain?.configuration) {
      project.data.managementDomain.configuration.hardwareConfiguration = change.hardwareConfiguration;
    }
  }

  if (project.data.output) {
    project.data.output.summary = undefined;
  }
  return project;
}

/**
 * Moved verbatim (semantics unchanged) from what used to be
 * ProjectDetailsState.recalculateClusterHardwareConfiguration's reducer body, back when the
 * cluster-scoped Recalculate/Save persisted immediately via its own
 * RecalculateClusterHardwareConfiguration dispatch instead of going through the staged
 * commitOutputSelections path like every other output-page save. Persists a Recalculate result
 * (ReadyNode for HCI/SON, ESXi for CON) onto the cluster entity's own canonical
 * hardwareConfiguration (the same field UpdateHciClusterConfiguration/
 * UpdateComputeClusterConfiguration/UpdateStorageClusterConfiguration write, so the wizard sees the
 * same value if reopened), AND fans it out to every workload referencing that cluster - both their
 * input-side local-mirror hardwareConfiguration (via syncReferencedHardwareConfiguration, so a
 * 'use-existing-cluster' referencer's sentinel survives exactly as it does in those wizard actions)
 * and their output-side selectedReadyNodeRecommendation.
 */
function applyClusterReadyNodeChange(
  project: VCPM.Project,
  change: Extract<StagedChange, { kind: 'cluster-readynode' }>,
): VCPM.Project | undefined {
  if (!project.data.workloadDomains) {
    return undefined;
  }
  const domain = project.data.workloadDomains.find((d) => String(d.id) === String(change.requirementsId));
  if (!domain) {
    return undefined;
  }
  // A Storage cluster (SON) is project-wide and may be referenced by workloads in OTHER domains
  // than the one it lives in - mirrors UpdateStorageClusterConfiguration's own project-wide scan.
  // HCI/Compute entities live inside their owning domain's own clusterConfigurations and can only
  // ever be referenced from within it, so this lookup is naturally single-domain for those kinds.
  const cluster = change.clusterKind === 'STORAGE'
    ? project.data.clusterConfigurations?.find((c) => c.id === change.clusterId)
    : domain.clusterConfigurations?.find((c) => c.id === change.clusterId && c.type === change.clusterKind);
  if (!cluster) {
    return undefined;
  }
  cluster.hardwareConfiguration = change.hardwareConfiguration;

  const clusterType = clusterEntityKindToClusterType(change.clusterKind);
  // Durable, cluster-scoped copy of this recalculation's computed output - unlike
  // workloadRecomm.selectedReadyNodeRecommendation below (shared across every workload
  // referencing this cluster, and clobbered when a mixed dHCI workload's OTHER side is saved
  // afterward), this lives on the cluster entity itself so the profile picker's dynamic 4th card
  // can be derived per-cluster instead of per-workload. Every member shares the same cluster-level
  // breakdown, so the first resolved result is representative.
  let representativeResult: PcsSizingEngineResponse.WorkloadRecommendation | undefined;
  resolveClusterMembers(project.data.workloadDomains, change.clusterId, clusterType).forEach((member) => {
    const memberDomain = project.data.workloadDomains!.find((d) => String(d.id) === member.requirementsId);
    const workload = memberDomain?.workloadList?.find((w) => w.id === member.workloadId);
    if (!workload) {
      return;
    }
    if (member.side === 'compute') {
      workload.computeClusterHardwareConfiguration = syncReferencedHardwareConfiguration(workload.computeClusterHardwareConfiguration, change.hardwareConfiguration);
    } else if (member.side === 'storage') {
      workload.storageClusterHardwareConfiguration = syncReferencedHardwareConfiguration(workload.storageClusterHardwareConfiguration, change.hardwareConfiguration);
    } else {
      workload.hardwareConfiguration = syncReferencedHardwareConfiguration(workload.hardwareConfiguration, change.hardwareConfiguration);
    }

    const result = change.resultsByWorkloadId.get(member.workloadId);
    representativeResult ??= result;
    const domainRecomm = project.data.output?.recommendation?.workloadDomainsRecommendation?.result?.recommendations
      ?.find((r: PcsSizingEngineResponse.Recommendation) => r.requirementsId === member.requirementsId);
    const workloadRecomm = domainRecomm?.workloadRecommendations.find((w: PcsSizingEngineResponse.WorkloadRecommendation) => w.workloadId === member.workloadId);
    if (result && workloadRecomm) {
      workloadRecomm.selectedReadyNodeRecommendation = {
        hardwareConfiguration: change.hardwareConfiguration,
        result,
        recalculatedAt: new Date().toISOString(),
        clusterType,
      };
      workloadRecomm.selectedProfileCode = AppConstants.SELECTED_READYNODE_PROFILE_CODE;
    }
  });
  if (representativeResult) {
    cluster.lastRecalculationResult = { result: representativeResult, recalculatedAt: new Date().toISOString() };
  }

  if (project.data.output) {
    project.data.output.summary = undefined;
  }
  return project;
}
